import React, { useState } from "react";
import { Alert, Col, Input, Modal, Radio, Row, Typography } from "antd";
import {
  ClearOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useConfirm } from "../../../hooks/useConfirm";
import { useMutations } from "../../../hooks/useApiMutation";
import { getMessage } from "../../../Utilities/messageApi";

const BASE = "/v1/admin/redis";

// Typing the word is the gate on the only action that clears everything at once. It is
// still recoverable — Redis holds nothing authoritative — but it should not be one stray
// click away.
const CONFIRM_WORD = "CLEAR";

const CALLER_TYPES = [
  { value: "apiKey", label: "API key", placeholder: "The X-Api-Key value the caller sends" },
  { value: "userId", label: "User id", placeholder: "Users.Id of the signed-in account" },
  { value: "ipAddress", label: "IP address", placeholder: "Remote address of an anonymous caller" },
];

const MaintenanceCard = ({ connected, onDone }) => {
  const { confirmAction } = useConfirm();
  const { mutate, loading } = useMutations();

  const [resetOpen, setResetOpen] = useState(false);
  const [callerType, setCallerType] = useState("apiKey");
  const [callerValue, setCallerValue] = useState("");

  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const report = (result, fallback) => {
    const removed = result?.keysRemoved ?? 0;
    getMessage()?.success(`${fallback} ${removed} key${removed === 1 ? "" : "s"} removed.`);
    onDone?.();
  };

  const clearEmbeddings = () =>
    confirmAction({
      key: "redis-clear-embeddings",
      method: "post",
      endpoint: `${BASE}/cache/embeddings/clear`,
      title: "Clear the embedding cache?",
      content:
        "Cached query vectors are dropped and recomputed on next use — which means paying the provider again for them. Nothing is lost.",
      okText: "Clear",
      onSuccess: (result) => report(result, "Embedding cache cleared."),
    });

  const clearRateLimits = () =>
    confirmAction({
      key: "redis-clear-ratelimits",
      method: "post",
      endpoint: `${BASE}/ratelimit/clear`,
      title: "Clear every rate-limit counter?",
      content:
        "Every caller starts its current minute from zero. The exposure is bounded by the window: at most one extra minute of quota, because counters expire on their own.",
      okText: "Clear",
      okType: "danger",
      onSuccess: (result) => report(result, "Rate-limit counters cleared."),
    });

  const submitReset = () => {
    const value = callerValue.trim();
    if (!value) return;

    mutate("redis-reset-caller", {
      method: "post",
      endpoint: `${BASE}/ratelimit/reset`,
      payload: { [callerType]: value },
      onSuccess: (result) => {
        setResetOpen(false);
        setCallerValue("");
        report(result, "Rate limit reset for that caller.");
      },
    });
  };

  const submitClearAll = () => {
    mutate("redis-clear-all", {
      method: "post",
      endpoint: `${BASE}/keys/clear`,
      onSuccess: (result) => {
        setClearAllOpen(false);
        setConfirmText("");
        report(result, "All platform keys removed.");
      },
    });
  };

  const activeType = CALLER_TYPES.find((t) => t.value === callerType);

  // One shape per action, so the grid stays even and adding a fifth is a data change.
  const actions = [
    {
      key: "redis-clear-embeddings",
      label: "Clear embedding cache",
      icon: <ClearOutlined />,
      onClick: clearEmbeddings,
      description:
        "Use after changing the embedding model or reworking the knowledge base — vectors from different models cannot be compared with each other.",
    },
    {
      key: "redis-reset-caller",
      label: "Reset one caller's limit",
      icon: <UserSwitchOutlined />,
      onClick: () => setResetOpen(true),
      description:
        "The way out when a single customer is stuck throttled, without touching anyone else.",
    },
    {
      key: "redis-clear-ratelimits",
      label: "Clear all rate-limit counters",
      icon: <ThunderboltOutlined />,
      onClick: clearRateLimits,
      description:
        "Everyone restarts the current minute. Bounded by the window — counters expire anyway.",
    },
    {
      key: "redis-clear-all",
      label: "Clear every platform key",
      icon: <DeleteOutlined />,
      variant: "danger",
      onClick: () => setClearAllOpen(true),
      description:
        "Caches, counters and fan-out state together. All rebuildable, but a blunt instrument — prefer one of the scoped actions above.",
    },
  ];

  return (
    <Card title="Maintenance">
      {!connected && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Redis is unreachable, so nothing can be cleared right now."
        />
      )}

      <Row gutter={[16, 16]}>
        {actions.map(({ key, label, icon, variant, onClick, description }) => (
          <Col key={key} xs={24} sm={12} xl={6}>
            {/* Full-height column so every description sits on the same baseline
                regardless of how many lines it wraps to. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, height: "100%" }}>
              <Button
                variant={variant ?? "secondary"}
                icon={icon}
                block
                disabled={!connected}
                loading={loading[key]}
                onClick={onClick}
              >
                {label}
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {description}
              </Typography.Text>
            </div>
          </Col>
        ))}
      </Row>

      <Modal
        open={resetOpen}
        title="Reset a caller's rate limit"
        okText="Reset"
        onOk={submitReset}
        confirmLoading={loading["redis-reset-caller"]}
        okButtonProps={{ disabled: !callerValue.trim() }}
        onCancel={() => setResetOpen(false)}
        centered
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
          Identify the caller the same way the API does. The value is hashed server-side
          before it touches Redis, so it never lands in the keyspace in readable form.
        </Typography.Paragraph>
        <Radio.Group
          value={callerType}
          onChange={(e) => setCallerType(e.target.value)}
          style={{ marginBottom: 12 }}
        >
          {CALLER_TYPES.map(({ value, label }) => (
            <Radio.Button key={value} value={value}>
              {label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Input
          value={callerValue}
          onChange={(e) => setCallerValue(e.target.value)}
          placeholder={activeType?.placeholder}
          onPressEnter={submitReset}
          autoFocus
        />
      </Modal>

      <Modal
        open={clearAllOpen}
        title="Clear every platform key?"
        okText="Clear everything"
        okButtonProps={{ danger: true, disabled: confirmText.trim() !== CONFIRM_WORD }}
        confirmLoading={loading["redis-clear-all"]}
        onOk={submitClearAll}
        onCancel={() => {
          setClearAllOpen(false);
          setConfirmText("");
        }}
        centered
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This removes every key under the platform's prefix"
          description="Cached embeddings will be recomputed and paid for again, every caller's rate-limit counter restarts, and in-flight real-time fan-out state is dropped. Another application sharing this Redis is not touched."
        />
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Type <Typography.Text code>{CONFIRM_WORD}</Typography.Text> to confirm.
        </Typography.Paragraph>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={CONFIRM_WORD}
          autoFocus
        />
      </Modal>
    </Card>
  );
};

export default MaintenanceCard;
